import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, imgSrc } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";

const NewCourse = ({ open, setOpen, onCreated }) => {
  const [f, setF] = useState({ title: "", description: "", cover_url: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      onCreated((await api.post("/courses", f)).data);
      setF({ title: "", description: "", cover_url: "" });
      setOpen(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white" data-testid="new-course-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New course</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input className="hb-input" placeholder="Course title" required value={f.title} onChange={set("title")} data-testid="course-title-input" />
          <textarea className="hb-input" placeholder="What will members learn?" value={f.description} onChange={set("description")} data-testid="course-description-input" />
          <div className="flex items-center gap-3">
            {f.cover_url && <img src={imgSrc(f.cover_url)} alt="Cover preview" className="w-28 aspect-video object-cover rounded-lg" data-testid="course-cover-preview" />}
            <ImageUpload testId="course-cover-upload" label={f.cover_url ? "Change cover" : "Upload cover (optional)"}
              onUploaded={(url) => setF((prev) => ({ ...prev, cover_url: url }))} />
          </div>
          <button className="btn-primary w-full" data-testid="course-save-button">Create course</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CourseCard = ({ c, i }) => {
  const pct = c.lesson_count ? Math.round((c.completed_count / c.lesson_count) * 100) : 0;
  return (
    <Link to={`/classroom/${c.id}`} className="hb-card overflow-hidden rise group hover:border-[var(--hb-ink)] transition-colors"
      style={{ animationDelay: `${i * 50}ms` }} data-testid={`course-card-${c.id}`}>
      <div className="relative aspect-video bg-[var(--hb-ink)] overflow-hidden grain">
        {c.cover_url ? (
          <img src={imgSrc(c.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <img src="/brand/logo.png" alt="" className="w-28 -rotate-6 group-hover:rotate-0 transition-transform duration-500" />
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl">{c.title}</h3>
        {c.description && <p className="text-sm text-[var(--hb-muted)] mt-1 line-clamp-2">{c.description}</p>}
        <div className="flex items-center gap-2 mt-4 text-xs text-[var(--hb-muted)]">
          <PlayCircle size={14} /> {c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"}
          <span className="ml-auto font-accent font-bold text-[var(--hb-ink)]" data-testid={`course-progress-${c.id}`}>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#F0F0EA] mt-2 overflow-hidden">
          <div className="h-full bg-[var(--hb-lime)] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </Link>
  );
};

export default function ClassroomPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
  }, []);

  return (
    <div data-testid="classroom-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-accent">Learn together</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Classroom</h1>
        </div>
        {user.role === "admin" && (
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="classroom-new-course-button"><Plus size={14} /> New course</button>
        )}
      </div>
      {courses?.length === 0 && (
        <div className="hb-card p-10 text-center mt-8" data-testid="classroom-empty">
          <p className="font-display text-xl">No courses yet.</p>
          <p className="text-sm text-[var(--hb-muted)] mt-1">
            {user.role === "admin" ? "Create a course, then paste your unlisted YouTube links as lessons." : "Fresh trainings are on the way, boo."}
          </p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {courses?.map((c, i) => <CourseCard key={c.id} c={c} i={i} />)}
      </div>
      <NewCourse open={open} setOpen={setOpen} onCreated={(c) => setCourses([...(courses || []), { ...c, lesson_count: 0, completed_count: 0 }])} />
    </div>
  );
}
